import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Button,
  Box,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close"; // ← Added icon
import Color from "../../components/Color";
import { useSnackbar } from "../../App";

const AddBranch = ({ open, onClose, formData, setFormData, onSubmit, editingId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showSnackbar } = useSnackbar();
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const codeInputRef = useRef(null);

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = ["Branch name is required"];
    } else if (!/^[a-zA-Z0-9 ]+$/.test(formData.name)) {
      newErrors.name = ["Branch name must contain only letters, numbers, and spaces"];
    } else if (formData.name.length > 50) {
      newErrors.name = ["Branch name must not exceed 50 characters"];
    }

    if (!formData.code.trim()) {
      newErrors.code = ["Branch code is required"];
    }

    if (!formData.phone.trim()) {
      newErrors.phone = ["Phone is required"];
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = ["Phone must be exactly 10 digits"];
    } else if (
      /^(\d)\1{9}$/.test(formData.phone) || // All digits same
      /^[01]/.test(formData.phone) // Starts with 0 or 1
    ) {
      newErrors.phone = ["Enter a valid mobile number"];
    }

    if (formData.email.trim() && 
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i.test(formData.email)
    ) {
      newErrors.email = ["Invalid email format"];
    }

    if (formData.address && formData.address.length > 250) {
      newErrors.address = ["Address must not exceed 250 characters"];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      showSnackbar("Please fix validation errors before submitting.", "error");
      return;
    }

    setLoading(true);
    try {
      await onSubmit(setErrors);
    } catch (err) {
      if (err.response?.data?.errors?.code) {
        codeInputRef.current?.focus();
        setErrors((prev) => ({
          ...prev,
          code: err.response.data.errors.code,
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: "",
      code: "",
      address: "",
      phone: "",
      email: "",
      is_active: true,
    });
    setErrors({});
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "name") {
      const filtered = value.replace(/[^a-zA-Z0-9 ]/g, "");
      const isValid = /^[a-zA-Z0-9 ]*$/.test(value);

      setFormData((prev) => ({
        ...prev,
        name: filtered.slice(0, 50),
      }));

      if (!filtered.trim()) {
        setErrors((prev) => ({ ...prev, name: ["Branch name is required"] }));
      } else if (!isValid && value !== "") {
        setErrors((prev) => ({
          ...prev,
          name: ["Only letters, numbers, and spaces are allowed"],
        }));
      } else if (filtered.length > 50) {
        setErrors((prev) => ({
          ...prev,
          name: ["Branch name must not exceed 50 characters"],
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.name;
          return newErrors;
        });
      }
    } else if (name === "phone") {
      const filteredValue = value.replace(/[^0-9]/g, "");
      setFormData((prev) => ({
        ...prev,
        phone: filteredValue.slice(0, 10), // Limit to 10 digits
      }));

      if (filteredValue.length === 10) {
        if (/^(\d)\1{9}$/.test(filteredValue) || /^[01]/.test(filteredValue)) {
          setErrors((prev) => ({
            ...prev,
            phone: ["Enter a valid mobile number"],
          }));
        } else {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.phone;
            return newErrors;
          });
        }
      } else if (filteredValue.length > 0) {
        setErrors((prev) => ({
          ...prev,
          phone: ["Phone must be exactly 10 digits"],
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          phone: ["Phone is required"],
        }));
      }
    } else if (name === "address") {
      const limitedValue = value.slice(0, 250);
      setFormData((prev) => ({
        ...prev,
        address: limitedValue,
      }));

      if (value.length > 250) {
        setErrors((prev) => ({
          ...prev,
          address: ["Address must not exceed 250 characters"],
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.address;
          return newErrors;
        });
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSwitchChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      is_active: e.target.checked,
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          if (loading) return;
          handleCancel();
        }
      }}
      disableEscapeKeyDown={loading}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: "8px",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: isMobile ? "1rem" : "1.25rem",
          position: "relative",
          pr: 6, // Padding right to make space for close button
        }}
      >
        {editingId ? "Edit Branch" : "Add Branch"}

        {/* Close Icon Button */}
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          disabled={loading}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: isMobile ? 2 : 3,
          py: isMobile ? 1 : 2,
          maxHeight: isMobile ? "70vh" : "auto",
          overflowY: "auto",
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Branch Name */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Branch Name*
              </Typography>
              <TextField
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter branch name (e.g. Main Branch 01)"
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.[0] || `${formData.name.length}/50`}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                inputProps={{ maxLength: 50 }}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                }}
              />
            </Box>

            {/* Branch Code */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Branch Code*
              </Typography>
              <TextField
                name="code"
                value={formData.code}
                onChange={handleChange}
                placeholder="Enter branch code"
                fullWidth
                error={!!errors.code}
                helperText={errors.code?.[0]}
                inputProps={{ maxLength: 10 }}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                inputRef={codeInputRef}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                }}
              />
            </Box>

            {/* Phone */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Phone*
              </Typography>
              <TextField
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter phone number"
                fullWidth
                error={!!errors.phone}
                helperText={errors.phone?.[0]}
                inputProps={{ maxLength: 10, inputMode: "numeric", pattern: "[0-9]*" }}
                type="tel"
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                }}
              />
            </Box>

            {/* Email */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Email (Optional)
              </Typography>
              <TextField
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.[0]}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                }}
              />
            </Box>

            {/* Address */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Address (Optional)
              </Typography>
              <TextField
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter address"
                fullWidth
                error={!!errors.address}
                helperText={errors.address?.[0] || `${formData.address.length}/250`}
                multiline
                rows={2}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                inputProps={{ maxLength: 250 }}
                sx={{
                  "& .MuiInputBase-input": { fontSize: isMobile ? "0.85rem" : "1rem" },
                }}
              />
            </Box>

            {/* Status Switch */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Status
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleSwitchChange}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: Color.primary.main,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: Color.primary.main,
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      color: formData.is_active ? Color.primary.main : "text.secondary",
                      fontWeight: formData.is_active ? "bold" : "normal",
                      fontSize: isMobile ? "0.85rem" : "1rem",
                    }}
                  >
                    {formData.is_active ? "Active" : "Inactive"}
                  </Typography>
                }
              />
            </Box>

            {errors.form && (
              <Typography color="error" variant="body2" mt={1}>
                {errors.form}
              </Typography>
            )}
          </Box>

          {/* Buttons */}
          <Box display="flex" justifyContent="flex-end" gap={2} mt={3} mb={1}>
            <Button
              onClick={handleCancel}
              sx={{
                background: "none",
                color: Color.secondary.main,
                border: "1px solid",
                borderColor: Color.secondary.main,
                width: isMobile ? "100%" : "auto",
                height: isMobile ? "40px" : "45px",
                fontSize: isMobile ? "0.85rem" : "1rem",
                padding: isMobile ? "8px" : "6px 16px",
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              sx={{
                background: Color.primary.main,
                color: "white",
                width: isMobile ? "100%" : "auto",
                height: isMobile ? "40px" : "45px",
                fontSize: isMobile ? "0.85rem" : "1rem",
                padding: isMobile ? "8px" : "6px 16px",
                "&:hover": {
                  background: Color.primary.dark,
                },
                "&.Mui-disabled": {
                  background: Color.primary.main,
                  opacity: 0.7,
                  color: "white",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : editingId ? (
                "Update"
              ) : (
                "Submit"
              )}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddBranch;